import { useState, useCallback } from 'react';
import axios from 'axios';
import { COMPLIANCE_ENDPOINTS } from '../constants/api';
import { calculateOverallCompletion } from '../utils/completionCalculations';
import {
  aggregateAgencyStats,
  aggregateCategoriesWithStats,
  calculateCompanyStats,
  AgencyUserStats,
} from '../utils/agencyStatsAggregation';

const getComplianceHeaders = () => {
  const token = sessionStorage.getItem('complianceToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Raw filing_field_values rows are { field_type_id, value, ... } — reshape into
// the { [fieldTypeId]: value } map calculateOverallCompletion/calculateCompanyStats expect.
const toFieldValueMap = (rows: any[] | undefined): Record<string, any> => {
  const map: Record<string, any> = {};
  (rows || []).forEach(row => {
    map[row.field_type_id] = row.value;
  });
  return map;
};

export const useComplianceDocumentStats = () => {
  const [agencyUsers, setAgencyUsers] = useState<AgencyUserStats[]>([]);
  const [agencyStats, setAgencyStats] = useState<ReturnType<typeof aggregateAgencyStats> | null>(null);
  const [categoriesWithStats, setCategoriesWithStats] = useState<ReturnType<typeof aggregateCategoriesWithStats>>([]);
  const [companyStats, setCompanyStats] = useState<ReturnType<typeof calculateCompanyStats> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAgencyDocumentStats = useCallback(async (agencyId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(
        COMPLIANCE_ENDPOINTS.AGENCY_DOCUMENT_COMPLIANCE_DATA(agencyId),
        { headers: getComplianceHeaders() }
      );

      const categories: any[] = data.categories || [];
      const subcategories: any[] = data.subcategories || [];
      const documentTypes: any[] = data.documentTypes || [];
      const fieldTypes: any[] = data.fieldTypes || [];
      const users: any[] = data.users || [];
      const companyDocuments: any[] = data.companyDocuments || [];
      const companyFieldValues: any[] = data.companyFieldValues || [];
      const userDocuments: Record<string, any[]> = data.userDocuments || {};
      const userFieldValues: Record<string, any[]> = data.userFieldValues || {};

      // filing_categories.type is 'employee' | 'company' — the manager dashboard's
      // agent stats only ever consider employee-type categories (see useDocumentMap's
      // AGENT_MAP fetch); company stats only ever consider company-type ones (AGENCY_MAP).
      const employeeCategoryIds = new Set(categories.filter(c => c.type === 'employee').map(c => c.id));
      const employeeCategories = categories.filter(c => employeeCategoryIds.has(c.id));
      const employeeSubcategories = subcategories.filter(s => employeeCategoryIds.has(s.category_id));
      const employeeDocumentTypes = documentTypes.filter(dt => employeeCategoryIds.has(dt.category_id));
      const employeeDocumentFields = fieldTypes.filter(f => employeeCategoryIds.has(f.category_id));

      const companyCategoryIds = new Set(categories.filter(c => c.type === 'company').map(c => c.id));
      const companyDocumentTypesAll = documentTypes.filter(dt => companyCategoryIds.has(dt.category_id));
      const companyDocumentFieldsAll = fieldTypes.filter(f => companyCategoryIds.has(f.category_id));

      const computedAgencyUsers: AgencyUserStats[] = users.map(user => {
        const documents = userDocuments[user.id] || [];
        const fieldValues = toFieldValueMap(userFieldValues[user.id]);
        const stats = calculateOverallCompletion(
          employeeCategories,
          employeeSubcategories,
          employeeDocumentTypes,
          employeeDocumentFields,
          documents,
          fieldValues
        );
        return { user, documents, fieldValues, stats };
      });

      // Matches DashboardContext.tsx's requiredDocumentTypesCount/requiredDocumentFieldsCount —
      // only required employee-type types/fields count toward the agent completion denominator.
      const agentDocumentTypesCount = employeeDocumentTypes.filter(dt => dt.required).length;
      const agentDocumentFieldsCount = employeeDocumentFields.filter(df => df.required).length;

      const computedAgencyStats = aggregateAgencyStats(computedAgencyUsers, agentDocumentTypesCount, agentDocumentFieldsCount);
      const computedCategoriesWithStats = aggregateCategoriesWithStats(employeeCategories, computedAgencyUsers);
      // Matches DashboardContext.tsx: company counts are ALL company types/fields, not filtered by .required.
      const computedCompanyStats = calculateCompanyStats(
        companyDocuments,
        toFieldValueMap(companyFieldValues),
        companyDocumentTypesAll.length,
        companyDocumentFieldsAll.length
      );

      setAgencyUsers(computedAgencyUsers);
      setAgencyStats(computedAgencyStats);
      setCategoriesWithStats(computedCategoriesWithStats);
      setCompanyStats(computedCompanyStats);

      return {
        agencyUsers: computedAgencyUsers,
        agencyStats: computedAgencyStats,
        categoriesWithStats: computedCategoriesWithStats,
        companyStats: computedCompanyStats,
      };
    } catch (err: any) {
      setError('Failed to fetch document compliance data');
      setAgencyUsers([]);
      setAgencyStats(null);
      setCategoriesWithStats([]);
      setCompanyStats(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    agencyUsers,
    agencyStats,
    categoriesWithStats,
    companyStats,
    loading,
    error,
    fetchAgencyDocumentStats,
  };
};
